import { useEffect, useState, FormEvent } from 'react';
import { adminApi, ApiError } from '../services/api';
import { buildFederatedLoginUrl } from '../utils/federatedAuth';
import { Plus, Copy, Check, ExternalLink, Loader2 } from 'lucide-react';

interface Application {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  redirectUris: string[];
  isActive: boolean;
  createdAt: string;
}

export function AdminApplicationsPage() {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [redirectUris, setRedirectUris] = useState('');

  const loadApplications = () => {
    setLoading(true);
    adminApi
      .getApplications()
      .then(({ applications: apps }) => setApplications(apps))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load applications'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadApplications();
  }, []);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const uris = redirectUris
        .split('\n')
        .map((u) => u.trim())
        .filter(Boolean);
      await adminApi.createApplication({ name, description: description || undefined, redirectUris: uris });
      setName('');
      setDescription('');
      setRedirectUris('');
      setShowForm(false);
      loadApplications();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create application');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm('Deactivate this application? Users will no longer be able to sign in through it.')) return;
    try {
      await adminApi.deactivateApplication(id);
      loadApplications();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to deactivate application');
    }
  };

  const copyClientId = async (clientId: string) => {
    await navigator.clipboard.writeText(clientId);
    setCopiedId(clientId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const exampleLoginUrl = (app: Application) => {
    const redirectUri = app.redirectUris[0];
    if (!redirectUri) return null;
    return `${window.location.origin}${buildFederatedLoginUrl({
      clientId: app.clientId,
      state: 'your-random-state-min-16-chars',
      codeChallenge: 'your-s256-code-challenge',
      redirectUri,
    })}`;
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Applications</h1>
          <p className="text-sm text-foreground-secondary mt-1">
            Register applications that authenticate users through this auth centre.
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Register application
        </button>
      </div>

      {error && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleCreate} className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold">New application</h2>
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="My App" />
          </div>
          <div>
            <label className="label">Description (optional)</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Internal dashboard" />
          </div>
          <div>
            <label className="label">Redirect URIs (one per line)</label>
            <textarea
              className="input min-h-[100px]"
              value={redirectUris}
              onChange={(e) => setRedirectUris(e.target.value)}
              required
              placeholder={'http://localhost:3000/auth/callback\nhttps://myapp.com/auth/callback'}
            />
            <p className="text-xs text-foreground-muted mt-1">
              After authentication, users are redirected here with an authorization code.
            </p>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Creating...' : 'Create application'}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        {applications.length === 0 ? (
          <div className="card p-6 text-sm text-foreground-secondary">No applications registered yet.</div>
        ) : (
          applications.map((app) => (
            <div key={app.id} className="card p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-foreground">{app.name}</h3>
                    {!app.isActive && (
                      <span className="text-xs px-2 py-0.5 rounded bg-foreground-muted/20 text-foreground-muted">
                        Inactive
                      </span>
                    )}
                  </div>
                  {app.description && (
                    <p className="text-sm text-foreground-secondary mt-1">{app.description}</p>
                  )}
                </div>
                {app.isActive && (
                  <button onClick={() => handleDeactivate(app.id)} className="text-sm text-error hover:underline">
                    Deactivate
                  </button>
                )}
              </div>

              <div>
                <p className="text-xs text-foreground-muted mb-1">Client ID</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-background-tertiary px-2 py-1 rounded font-mono">{app.clientId}</code>
                  <button
                    onClick={() => copyClientId(app.clientId)}
                    className="text-foreground-muted hover:text-foreground"
                    title="Copy client ID"
                  >
                    {copiedId === app.clientId ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <p className="text-xs text-foreground-muted mb-1">Redirect URIs</p>
                <ul className="text-sm text-foreground-secondary space-y-1">
                  {app.redirectUris.map((uri) => (
                    <li key={uri} className="font-mono text-xs bg-background-tertiary px-2 py-1 rounded inline-block mr-2">
                      {uri}
                    </li>
                  ))}
                </ul>
              </div>

              {app.isActive && exampleLoginUrl(app) && (
                <div>
                  <p className="text-xs text-foreground-muted mb-1">Login entry point (send users here)</p>
                  <div className="flex items-start gap-2">
                    <code className="text-xs bg-background-tertiary px-2 py-1 rounded font-mono break-all flex-1">
                      {exampleLoginUrl(app)}
                    </code>
                    <a
                      href={exampleLoginUrl(app)!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent hover:text-accent-hover mt-1"
                      title="Test login flow"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
